import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IdentificationListing from '../identifications/indentificationListing';
import MatterRequestGeneralCard from './matterRequestGeneralCard';
import { useLocation, useParams } from 'react-router-dom';
import { MatterRequestSections, MatterTabSections } from '../../interface/matter';
import Notes from '../conflictSearch/notes';
import { NotesEntity } from '../../constants/notes';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useFetchMatterRequestsById } from '../../hooks/useFetchMatterRequestById';
import { IMatterRequest } from '../../interface/matterRequest';
import PartiesListing from '../parties/partiesListing';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useUser } from '../../context/UserContext';
import { UserType } from '../../constants/constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomTabs from '../customTabs/customTabs';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';

const MatterRequestDetailView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { goBack, updateTop } = useNavigationStack();
  const { setTitle } = usePage();
  const { user } = useUser();
  const { trackPage } = useAnalytics();
  const fromTab = location?.state?.fromTab ?? MatterRequestSections.general;
  const [selectedTab, setSelectedTab] = useState<string>(fromTab.toString());

  const {
    data: matterRequest,
    isLoading: isLoadingMatterRequest,
    refetch,
  } = useFetchMatterRequestsById({
    id,
    relations: [
      'ownerUser',
      'responsibleUser',
      'businessArea',
      'matterRequestType',
      'parties',
      'parties.company',
      'parties.privatePerson',
    ],
    fields: [
      'status',
      'createdOn',
      'checkedOnDate',
      'subject',
      'language',
      'currency',
      'description',
      'hasImportantNotes',
      'hasNotes',
      'matterRequestTypeId',
      'ownerUserId',
      'responsibleUserId',
      'assistantUserId',
      'invoicingUserId',
      'insider',
      'businessAreaId',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.MATTER_REQUEST_DETAIL);
  }, [trackPage]);
  const handleBack = () => {
    goBack({ fromTab: MatterTabSections.matterRequests });
  };
  useEffect(() => {
    updateTop({ fromTab: selectedTab });
  }, [selectedTab]);
  useEffect(() => {
    if (matterRequest && !isLoadingMatterRequest) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={handleBack}
          title={matterRequest?.subject}
          subTitle={t('common:tab.matterRequests')}
        />,
      );
    }
  }, [matterRequest, isLoadingMatterRequest]);
  const items = useMemo(
    () => [
      {
        key: MatterRequestSections.general.toString(),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <MatterRequestGeneralCard
              matterRequest={matterRequest as IMatterRequest}
              isLoading={isLoadingMatterRequest}
              refetchMatterRequest={async () => (await refetch()).data}
            />
          </DetailScrollArea>
        ),
      },

      {
        key: MatterRequestSections.parties.toString(),
        label: t('common:tab.parties'),
        children: <PartiesListing type="matterRequest" />,
      },
      ...(user?.userType !== UserType.RESTRICTED
        ? [
            {
              key: MatterRequestSections.identification.toString(),
              label: t('common:tab.identifications'),
              children: <IdentificationListing type="matterRequest" />,
            },
          ]
        : []),
      {
        key: MatterRequestSections.notes.toString(),
        label: t('common:tab.notes'),
        children: (
          <Notes entitySubject={matterRequest?.subject ?? ''} entity={NotesEntity.MATTERREQUEST} />
        ),
      },
    ],
    [t, matterRequest],
  );
  return (
    <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />
  );
};

export default MatterRequestDetailView;
